import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "../styles/Sidebar.css";

const menu = [
  {
    key: "my-work",
    label: "My Work",
    icon: "grid_view",
    path: "#",
    submenu: [
      { key: "home",      label: "Home",      icon: "home",           path: "/home"                },
      { key: "dashboard", label: "Dashboard", icon: "dashboard",      path: "/dashboard"           },
      { key: "reports",   label: "Reports",   icon: "analytics",      path: "/reports"             },
      { key: "tasks",     label: "Tasks",     icon: "task",           path: "/tasks"               },
      { key: "calendar",  label: "Calendar",  icon: "calendar_today", path: "/activities/calendar" },
      { key: "notes",     label: "Notes",     icon: "note",           path: "/activities/notes"    },
    ],
  },
  {
    key: "people",
    label: "People",
    icon: "group",
    path: "#",
    submenu: [
      { key: "contacts", label: "Contacts", icon: "contacts",        path: "/contacts"        },
      { key: "vendors",  label: "Vendors",  icon: "store",           path: "/people/vendors"  },
      { key: "lenders",  label: "Lenders",  icon: "account_balance", path: "/people/lenders"  },
    ],
  },
  {
    key: "inbox",
    label: "Inbox",
    icon: "mail",
    path: "#",
    submenu: [
      { key: "email", label: "Email", icon: "email", path: "/inbox/email" },
      { key: "sms",   label: "SMS",   icon: "sms",   path: "/inbox/sms"   },
    ],
  },
  {
    key: "deals",
    label: "Deals",
    icon: "handshake",
    path: "#",
    submenu: [
      { key: "pipeline",     label: "Pipeline",     icon: "view_stream",  path: "/deals/pipeline"     },
      { key: "transactions", label: "Transactions", icon: "receipt_long", path: "/deals/transactions" },
    ],
  },
  {
    key: "properties",
    label: "Properties",
    icon: "home_work",
    path: "#",
    submenu: [
      { key: "property-search",     label: "Property Search",     icon: "search",      path: "/properties/search"   },
      { key: "property-drip",       label: "Property Drip",       icon: "water_drop",  path: "/properties/drip"     },
      { key: "my-listings",         label: "My Listings",         icon: "list_alt",    path: "/properties/listings" },
      { key: "reverse-prospecting", label: "Reverse Prospecting", icon: "trending_up", path: "/properties/reverse"  },
    ],
  },
  {
    key: "marketing",
    label: "Marketing",
    icon: "campaign",
    path: "#",
    submenu: [
      { key: "social",            label: "Social",            icon: "share",     path: "/marketing/social"         },
      { key: "blog",              label: "Blog",              icon: "article",   path: "/marketing/blog"           },
      { key: "meta-ads",          label: "Meta Ads",          icon: "ads_click", path: "/marketing/meta-ads"       },
      { key: "sms-marketing",     label: "SMS",               icon: "message",   path: "/marketing/sms"            },
      { key: "zipper-ai-copilot", label: "Zipper AI Copilot", icon: "smart_toy", path: "/marketing/ai-copilot"     },
      { key: "drip-campaigns",    label: "Drip Campaigns",    icon: "autorenew", path: "/marketing/drip-campaigns" },
      { key: "bulk-sms",          label: "Bulk SMS",          icon: "send",      path: "/marketing/bulk-sms"       },
    ],
  },
];

const PREFETCH_KEY_MAP = {
  home:        "home",
  dashboard:   "dashboard",
  reports:     "reports",
  contacts:    "contacts",
  calendar:    "calendar",
  notes:       "notes",
  email:       "email",
  automations: "automations",
  settings:    "settings",
};

export default function Sidebar({ currentUser, preloadRoute, collapsed: controlledCollapsed, onCollapsedChange }) {
  // If controlledCollapsed is provided, use controlled mode; otherwise use internal state
  const isControlled = controlledCollapsed !== undefined;
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const collapsed = isControlled ? controlledCollapsed : internalCollapsed;

  const [openKeys, setOpenKeys] = useState({});
  const location = useLocation();

  useEffect(() => {
    const newOpenKeys = {};
    menu.forEach((m) => {
      if (m.submenu) {
        const isSubActive = m.submenu.some((s) =>
          s.path === "/home"
            ? location.pathname === "/home"
            : location.pathname.startsWith(s.path)
        );
        if (isSubActive) newOpenKeys[m.key] = true;
      }
    });
    setOpenKeys(newOpenKeys);
  }, [location.pathname]);

  const toggleSidebar = () => {
    const next = !collapsed;
    if (isControlled) {
      onCollapsedChange && onCollapsedChange(next);
    } else {
      setInternalCollapsed(next);
    }
  };

  const toggleSubmenu = (key) => {
    setOpenKeys((prev) => ({ [key]: !prev[key] }));
  };

  const isItemActive = (s) => {
    if (s.path === "/home") return location.pathname === "/home";
    return location.pathname.startsWith(s.path);
  };

  const handleMouseEnter = (key) => {
    if (preloadRoute && PREFETCH_KEY_MAP[key]) {
      preloadRoute(PREFETCH_KEY_MAP[key]);
    }
  };

  return (
    <aside className={`sidebar ${collapsed ? "collapsed" : ""}`}>
      <div className="sidebar-content">

        {/* Logo */}
        <div className="sidebar-logo">
          <img src="/images/za-logo-all.png"  alt="ZipperAgent" className="sidebar-logo-full" />
          <img src="/images/za-logo-icon.png" alt="ZA"          className="sidebar-logo-icon" />
        </div>

        {/* Main nav */}
        <nav className="menu-scroll">
          <ul className="menu-section">
            {menu.map((m) => {
              const isActive =
                (m.path && m.path !== "#" && location.pathname.startsWith(m.path)) ||
                (m.submenu && m.submenu.some((s) => isItemActive(s)));

              const hasSubmenu = m.submenu && m.submenu.length > 0;
              const isOpen     = !!openKeys[m.key];

              return (
                <li key={m.key} className="menu-item">
                  <div className={`menu-link ${isActive ? "active" : ""}`}>
                    <Link
                      to={m.path === "#" ? "#" : m.path}
                      className="menu-content"
                      onClick={(e) => {
                        if (hasSubmenu) { e.preventDefault(); toggleSubmenu(m.key); }
                      }}
                    >
                      <span className="material-symbols-outlined menu-icon">{m.icon}</span>
                      <span className="menu-label">{m.label}</span>
                    </Link>

                    {hasSubmenu && (
                      <span
                        className={`chevron ${isOpen ? "open" : ""}`}
                        onClick={(e) => { e.preventDefault(); toggleSubmenu(m.key); }}
                      >
                        <span className="material-symbols-outlined">chevron_right</span>
                      </span>
                    )}
                  </div>

                  {hasSubmenu && (
                    <ul className={`submenu ${isOpen ? "open" : ""}`}>
                      {m.submenu.map((s) => (
                        <li
                          key={s.key}
                          className={`submenu-item ${isItemActive(s) ? "active" : ""}`}
                        >
                          <Link
                            to={s.path}
                            onMouseEnter={() => handleMouseEnter(s.key)}
                          >
                            <span className="material-symbols-outlined submenu-icon">{s.icon}</span>
                            <span className="submenu-label">{s.label}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      </div>

      {/* Bottom links */}
      <div className="sidebar-bottom">

        <Link
          to="/admin"
          className={`bottom-link ${location.pathname.startsWith("/admin") ? "active" : ""}`}
          onMouseEnter={() => preloadRoute && preloadRoute("admin")}
        >
          <span className="material-symbols-outlined">admin_panel_settings</span>
          <span className="bottom-text">Admin</span>
        </Link>

      </div>

      <button className="sidebar-toggle" onClick={toggleSidebar}>
        <span className="material-symbols-outlined">
          {collapsed ? "chevron_right" : "chevron_left"}
        </span>
      </button>
    </aside>
  );
}